import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class RememberMeStorageService {
  private readonly REMEMBER_TOKEN_KEY = 'remember_token';
  private readonly DEVICE_FINGERPRINT_KEY = 'device_fingerprint';
  private readonly REMEMBER_EXPIRES_KEY = 'remember_expires_at';

  constructor(private logger: LoggerService) {}

  setRememberToken(token: string, expiresAt?: string): void {
    try {
      localStorage.setItem(this.REMEMBER_TOKEN_KEY, token);
      if (expiresAt) {
        localStorage.setItem(this.REMEMBER_EXPIRES_KEY, expiresAt);
      }
      this.logger.info('Remember Me token stored successfully');
    } catch (error) {
      this.logger.error('Failed to store Remember Me token:', error);
    }
  }

  getRememberToken(): string | null {
    try {
      const token = localStorage.getItem(this.REMEMBER_TOKEN_KEY);

      if (!token) {
        return null;
      }

      const expiresAt = localStorage.getItem(this.REMEMBER_EXPIRES_KEY);
      if (expiresAt) {
        const expiryDate = new Date(expiresAt);
        if (expiryDate < new Date()) {
          this.logger.warn('Remember Me token expired, clearing...');
          this.clearRememberToken();
          return null;
        }
      }

      return token;
    } catch (error) {
      this.logger.error('Failed to retrieve Remember Me token:', error);
      return null;
    }
  }

  clearRememberToken(): void {
    try {
      localStorage.removeItem(this.REMEMBER_TOKEN_KEY);
      localStorage.removeItem(this.REMEMBER_EXPIRES_KEY);
      this.logger.info('Remember Me token cleared');
    } catch (error) {
      this.logger.error('Failed to clear Remember Me token:', error);
    }
  }

  setDeviceFingerprint(fingerprint: string): void {
    try {
      localStorage.setItem(this.DEVICE_FINGERPRINT_KEY, fingerprint);
    } catch (error) {
      this.logger.error('Failed to store device fingerprint:', error);
    }
  }

  getDeviceFingerprint(): string | null {
    try {
      return localStorage.getItem(this.DEVICE_FINGERPRINT_KEY);
    } catch (error) {
      this.logger.error('Failed to retrieve device fingerprint:', error);
      return null;
    }
  }

  hasValidRememberToken(): boolean {
    return this.getRememberToken() !== null;
  }

  getRememberTokenExpiry(): Date | null {
    const expiresAt = localStorage.getItem(this.REMEMBER_EXPIRES_KEY);
    return expiresAt ? new Date(expiresAt) : null;
  }

  getRemainingDays(): number | null {
    const expiry = this.getRememberTokenExpiry();
    if (!expiry) {
      return null;
    }

    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  clearAll(): void {
    this.clearRememberToken();
    try {
      localStorage.removeItem(this.DEVICE_FINGERPRINT_KEY);
    } catch (error) {
      this.logger.error('Failed to clear device fingerprint:', error);
    }
  }
}
